import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Image,
  Popconfirm,
  Tag,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import adminApiService from '../../services/adminApi';

interface Article {
  id: string;
  title: string;
  title_en: string;
  content: string;
  content_en: string;
  summary: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  publishedAt: string;
  category: string;
  tags: string[];
  readTime: string;
  slug: string;
  status: string;
  pdfFile?: string;
}

const ArticlesPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [form] = Form.useForm();
  const [uploadedImagePath, setUploadedImagePath] = useState<string>('');
  const [uploadedPdfPath, setUploadedPdfPath] = useState<string>('');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getData('articles');
      setArticles(response.data.articles || []);
    } catch (error) {
      message.error('加载文章数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingArticle(null);
    form.resetFields();
    setUploadedImagePath('');
    setUploadedPdfPath('');
    setModalVisible(true);
  };

  const handleEdit = (article: Article) => {
    setEditingArticle(article);
    form.setFieldsValue({
      ...article,
      tags: article.tags?.join(', ') || '',
      date: article.date ? dayjs(article.date) : undefined,
      publishedAt: article.publishedAt ? dayjs(article.publishedAt) : undefined,
    });
    setUploadedImagePath(article.image);
    setUploadedPdfPath(article.pdfFile || '');
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const newArticles = articles.filter((a) => a.id !== id);
      await adminApiService.updateData('articles', { articles: newArticles });
      message.success('删除成功');
      loadArticles();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const newArticle: Article = {
        id: editingArticle?.id || String(Date.now()),
        title: values.title,
        title_en: values.title_en,
        content: values.content,
        content_en: values.content_en,
        summary: values.summary || '',
        excerpt: values.excerpt,
        image: uploadedImagePath || values.image,
        author: values.author,
        date: values.date ? dayjs(values.date).format('YYYY-MM-DD') : '',
        publishedAt: values.publishedAt
          ? dayjs(values.publishedAt).format('YYYY-MM-DD')
          : '',
        category: values.category,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : [],
        readTime: values.readTime,
        slug: values.slug || '',
        status: values.status || 'published',
        pdfFile: uploadedPdfPath || values.pdfFile,
      };

      let newArticles: Article[];
      if (editingArticle) {
        newArticles = articles.map((a) =>
          a.id === editingArticle.id ? newArticle : a
        );
      } else {
        newArticles = [...articles, newArticle];
      }

      await adminApiService.updateData('articles', { articles: newArticles });
      message.success(editingArticle ? '更新成功' : '添加成功');
      setModalVisible(false);
      loadArticles();
    } catch (error: any) {
      message.error(error.message || '保存失败');
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const response = await adminApiService.uploadFile(file, 'images', 'articles');
      setUploadedImagePath(response.path);
      message.success('图片上传成功');
      return false;
    } catch (error) {
      message.error('图片上传失败');
      return false;
    }
  };

  const handlePdfUpload = async (file: File) => {
    try {
      const response = await adminApiService.uploadFile(file, 'pdf');
      setUploadedPdfPath(response.path);
      message.success('PDF 上传成功');
      return false;
    } catch (error) {
      message.error('PDF 上传失败');
      return false;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image: string) => (
        <Image src={image} alt="article" className="image-preview" />
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 100,
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => <Tag color="green">{category}</Tag>,
    },
    {
      title: 'PDF',
      dataIndex: 'pdfFile',
      key: 'pdfFile',
      width: 80,
      render: (pdf: string) => (pdf ? '是' : '否'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Article) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2 className="page-header">文章管理</h2>
      <div className="action-buttons">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加文章
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={articles}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingArticle ? '编辑文章' : '添加文章'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="标题（中文）"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="标题（英文）"
            name="title_en"
            rules={[{ required: true, message: '请输入英文标题' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="内容（中文）"
            name="content"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            label="内容（英文）"
            name="content_en"
            rules={[{ required: true, message: '请输入英文内容' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="摘要" name="summary">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="简介" name="excerpt">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="图片路径" name="image">
            <Input
              placeholder="图片路径或上传图片"
              value={uploadedImagePath}
              onChange={(e) => setUploadedImagePath(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="上传图片">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>点击上传</Button>
            </Upload>
            {uploadedImagePath && (
              <div style={{ marginTop: 8 }}>
                <Image src={uploadedImagePath} alt="preview" width={200} />
              </div>
            )}
          </Form.Item>
          <Form.Item label="PDF 文件路径" name="pdfFile">
            <Input
              placeholder="PDF 路径或上传文件"
              value={uploadedPdfPath}
              onChange={(e) => setUploadedPdfPath(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="上传 PDF">
            <Upload
              beforeUpload={handlePdfUpload}
              showUploadList={false}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />}>点击上传 PDF</Button>
            </Upload>
            {uploadedPdfPath && <div style={{ marginTop: 8 }}>{uploadedPdfPath}</div>}
          </Form.Item>
          <Form.Item
            label="作者"
            name="author"
            rules={[{ required: true, message: '请输入作者' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="日期" name="date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="发布日期" name="publishedAt">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请输入分类' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="标签（逗号分隔）" name="tags">
            <Input placeholder="标签1, 标签2, 标签3" />
          </Form.Item>
          <Form.Item label="阅读时间" name="readTime">
            <Input placeholder="例如：5分钟" />
          </Form.Item>
          <Form.Item label="Slug" name="slug">
            <Input />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Input placeholder="published / draft" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ArticlesPage;
